'use client';
import { createEssentials } from '@wix/fe-essentials-standalone';
import { RootEssentialsProvider } from '@wix/fe-essentials-standalone/react';
import {
  I18nextProvider,
  Messages,
  initI18n,
} from '@wix/fe-essentials-standalone/i18n';
import type { ExperimentsBag } from '@wix/wix-experiments';
import { WixDesignSystemProvider } from '@wix/design-system';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { AppContextProvider } from '@components/Contexts/AppContext';

export default function Providers({
  children,
  ...props
}: {
  children: React.ReactNode;
  language: string;
  artifactId: string;
  artifactVersion: string;
  messages: Messages | undefined;
  experiments: ExperimentsBag;
  baseURL: string;
  isMobileUserAgent: boolean;
  isLoggedIn: boolean;
}) {
  // useState ensures one client per request (no cross-request cache leaks)
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { refetchOnWindowFocus: false } },
      }),
  );

  const essentials = createEssentials({
    environment: {
      language: props.language,
      artifactId: props.artifactId,
      version: props.artifactVersion,
    },
    experiments: { bag: props.experiments },
    fedops: {},
    errorMonitor: {
      dsn: 'https://7236953e4400460e850ac619ca1c45aa@sentry.wixpress.com/3949',
    },
    httpClient: {
      baseURL: props.baseURL,
    },
  });

  const i18n = initI18n({
    locale: props.language,
    messages: props.messages,
  });

  return (
    <QueryClientProvider client={queryClient}>
      <RootEssentialsProvider essentials={essentials}>
        <I18nextProvider i18n={i18n}>
          <AppContextProvider
            isMobileUserAgent={props.isMobileUserAgent}
            isLoggedIn={props.isLoggedIn}
          >
            <WixDesignSystemProvider features={{ newColorsBranding: true }}>
              {children}
            </WixDesignSystemProvider>
          </AppContextProvider>
        </I18nextProvider>
      </RootEssentialsProvider>
    </QueryClientProvider>
  );
}
